import { HttpErrorResponse, HttpRequest } from '@angular/common/http';

/** AIWB-1175 — see design/auth/2026-10-06-aiwb1175-outage-hold-replay-spec.md. */
/**
 * From here the secondary exits appear (Stop waiting / Reload). No "Go to home" here: with Core down,
 * home fails to load the same way.
 */
export const OUTAGE_ESCAPE_MS = 30_000;
/** From here the "still working on it" copy, the warning clock and the primary Check now button appear. */
export const OUTAGE_SLOW_MS = 60_000;
/** From here polling slows to every 30 s. It no longer changes anything on screen. */
export const OUTAGE_SLOW_POLL_MS = 5 * 60 * 1000;
export const OUTAGE_OK_STREAK = 2;
const OUTAGE_STATUSES = [0, 502, 503, 504];

export const OUTAGE_COPY = {
  outageTitle: 'AIWB will be right back',
  outageBody:
    'We\x27re getting things back up and running. ' +
    'Your work is safe — you\x27ll carry on right where you left off.',
  reconnecting: 'Reconnecting automatically',
  slowTitle: 'Still working on it',
  slowBody:
    'AIWB is taking a little longer to come back. ' +
    'Your work is safe — you\x27ll carry on automatically, or choose an option below.',
  slowStatus: 'We check again every few seconds',
  upgradeTitle: 'We\x27re upgrading AIWB',
  upgradeBody:
    'A better AIWB is on its way. ' + 'Your work is safe — you\x27ll carry on right where you left off in a moment.',
  upgradeSlowBody:
    'The upgrade is taking a little longer. ' +
    'Your work is safe — you\x27ll carry on automatically, or choose an option below.',
  upgradeStatus: 'Usually takes a minute or two',
  exitsHeading: 'Don\x27t want to wait?',
  exitsNote: 'Reload discards changes that haven\x27t been sent yet.',
  checkNow: 'Check now',
  stopWaiting: 'Stop waiting',
  reload: 'Reload',
  checking: 'Checking…',
  stillUnreachable: 'Still unreachable — we\x27ll keep trying automatically',
  backOnline: 'Back online — reconnecting…',
  availableSoon: 'Available again in a moment',
  uncertainBadge: 'AIWB is back',
  uncertainTitle: 'Your last change may already have been saved',
  uncertainBody:
    'The connection dropped while it was being sent. Send it again, or leave it and check first — ' +
    'your form still has everything you entered.',
  sendAgain: 'Send again',
  dontSend: 'Don\x27t send',
  backTitle: 'AIWB is back',
  backBody: 'You can carry on.',
} as const;

/** Deploy marker: an upgrading platform answers `x-aiwb-maintenance: deploy` (no backend sends it yet). */
export function isDeployMarker(error: HttpErrorResponse): boolean {
  return (error.headers?.get('x-aiwb-maintenance') ?? '').trim().toLowerCase() === 'deploy';
}

/** The load balancer answers an outage with HTML (or nothing); Core's own 5xx are JSON. */
export function isOutageCandidate(error: unknown): error is HttpErrorResponse {
  if (!(error instanceof HttpErrorResponse) || !OUTAGE_STATUSES.includes(error.status)) {
    return false;
  }
  return !(error.headers?.get('content-type') ?? '').toLowerCase().includes('json');
}

export type ReplayDecision = 'replay' | 'uncertain';

/** Spec §4.4. A 503 from the ALB means no target — the request never reached Core. */
export function replayPolicy(req: HttpRequest<unknown>, status: number): ReplayDecision {
  if (req.method === 'GET' || req.method === 'HEAD' || status === 503) {
    return 'replay';
  }
  if (req.headers.has('If-Match') || req.url.includes('/auth/guest/session')) {
    return 'replay';
  }
  return 'uncertain';
}

export function pollDelay(elapsedMs: number, okStreak: number, random: () => number = Math.random): number {
  const base = okStreak > 0 ? 2000 : elapsedMs < 60_000 ? 4000 : elapsedMs < OUTAGE_SLOW_POLL_MS ? 10_000 : 30_000;
  return Math.round(base * (0.75 + random() * 0.5));
}

export function isPingUrl(url: string): boolean {
  return /\/api\/ping(\?|$)/.test(url);
}

/** `GET /api/ping` must answer the text `OK` — bare `/ping` returns the app's HTML even when Core is down. */
export async function pingPlatform(fetchFn: typeof fetch = fetch.bind(window)): Promise<boolean> {
  try {
    const res = await fetchFn('/api/ping', { cache: 'no-store', credentials: 'include' });
    return res.ok && (await res.text()).trim() === 'OK';
  } catch {
    return false;
  }
}
